import FacebookLogin from "@greatsumini/react-facebook-login";
import { useGoogleLogin } from "@react-oauth/google";
import React, { useEffect, useRef, useState } from "react";
import { BiLogoFacebookCircle } from "react-icons/bi";
import { FcGoogle } from "react-icons/fc";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fbAppId } from "../../constants/environment.constants";
import { ERR_MSGS } from "../../constants/errorMsgs";
import {
  initLoginFormCredentials,
  initLoginFormErrors,
} from "../../constants/initialForms";
import { QUERY_PARAMS_KEY, STORAGE_KEYS } from "../../constants/keys";
import { regexPatterns } from "../../constants/regexPatterns";
import { fetchAccountInfo, fetchParentInfo, fetchStaffInfo, signIn, verifyProvisionedUser } from "../../services/auth.service";
import ResetPasswordModal from "../modals/ReastPasswordModal";
import RequiredAsterisk from "../ui/icons/RequiredAsterisk";
import { USER_ROLES } from "../../constants/roles";
import VerifyYourEmailModal from "../modals/VerifyYourEmailModal";
import DeactivationModal from "../modals/DeactivationModal";
import AccountTypeModal from "../modals/AccountTypeModal";
import MfaChallengeModal from "../modals/MfaChallengeModal";

type ProvisionedVerifyState = "idle" | "verifying" | "invalid_token" | "already_verified" | "not_found";
type MfaSource = "password" | "accountType" | "social";

const LoginForm = (props: any) => {
  const [loginForm, setLoginForm] = useState(initLoginFormCredentials);
  const [loginFormErrors, setLoginFormErrors] = useState(initLoginFormErrors);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [deactivationModal, setDeactivationModal] = useState<
    "user" | "center" | "parent" | null
  >(null);
  const [showAccountTypeModal, setShowAccountTypeModal] = useState(false);
  const [multipleAccounts, setMultipleAccounts] = useState<any[]>([]);
  const [isSelectingAccountType, setIsSelectingAccountType] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState<{
    challengeId: string;
    accountType: string;
    availableChannels: ("email" | "sms")[];
    // "enroll": this login came back mfaEnrollmentRequired (grace logins
    // exhausted) rather than mfaRequired (account already had MFA on) —
    // same code-entry mechanics, different framing/confirm endpoint inside
    // MfaChallengeModal.
    mode: "login" | "enroll";
    source: MfaSource;
  } | null>(null);
  const [provisionedVerifyState, setProvisionedVerifyState] = useState<ProvisionedVerifyState>("idle");
  const hasVerifiedProvisionedRef = useRef(false);
  const urlParams = new URLSearchParams(window.location.search);
  const redirectURI = urlParams.get(QUERY_PARAMS_KEY.REDIRECT_URI);
  const clientID = urlParams.get(QUERY_PARAMS_KEY.CLIENT_ID);

  // The BE appends ?token=... onto the redirect_URI value, so the token ends up
  // embedded inside redirect_URI rather than as a top-level param.
  // e.g. redirect_URI=http://localhost:3001/auth/verify?token=eyJ...
  const provisionedToken = (() => {
    const direct = urlParams.get(QUERY_PARAMS_KEY.TOKEN);
    if (direct) return direct;
    try {
      return redirectURI ? new URL(redirectURI).searchParams.get("token") : null;
    } catch {
      return null;
    }
  })();

  // redirect_URI with the embedded token stripped, for use after verification
  const cleanRedirectURI = (() => {
    if (!redirectURI) return redirectURI;
    try {
      const url = new URL(redirectURI);
      url.searchParams.delete("token");
      return url.toString();
    } catch {
      return redirectURI;
    }
  })();
  const isEditing = JSON.parse(urlParams.get(QUERY_PARAMS_KEY.EDIT) as string);
  const cancelURI = urlParams.get(QUERY_PARAMS_KEY.CANCEL_URI);
  const prevUser = JSON.parse(
    localStorage.getItem(STORAGE_KEYS.PREV_USER) as string,
  );
  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = React.useState(false);

  const openModal = () => setModalOpen(true);
  const closeModal = () => {
    setModalOpen(false);
    localStorage.removeItem(STORAGE_KEYS.PREV_USER);
  };
  const handleRememberMeChange = () => {
    setRememberMe((prev) => !prev);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event?.target?.name;
    const value = event?.target?.value;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
    setLoginFormErrors((prev) => ({ ...prev, loginError: false }));
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const key =
      event?.target?.name === "email" ? "emailError" : "passwordError";
    setLoginFormErrors((prev) => ({ ...prev, [key]: "", loginError: false }));
  };

  useEffect(() => {
    if (!prevUser) return;
    openModal();
  }, [prevUser]);

  useEffect(() => {
    if (showVerificationModal) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [showVerificationModal]);

  useEffect(() => {
    if (!provisionedToken) return;
    // The verification token is single-use; React.StrictMode double-invokes
    // effects in development, so guard against firing the verify call twice.
    if (hasVerifiedProvisionedRef.current) return;
    hasVerifiedProvisionedRef.current = true;
    const handleProvisionedVerification = async () => {
      setProvisionedVerifyState("verifying");
      const res = await verifyProvisionedUser(provisionedToken);
      if (res?.ok) {
        const authCode = res?.data?.authCode;
        if (authCode) {
          window.location.replace(
            `${cleanRedirectURI}?authCode=${authCode}&remember_me=false`,
          );
        } else {
          window.location.replace(cleanRedirectURI as string);
        }
        return;
      }
      const code = res?.errorCode;
      if (code === "is_already_verified") {
        setProvisionedVerifyState("already_verified");
        navigate(
          `/sign-in${clientID ? "?client_id=" + clientID : ""}${redirectURI ? "&redirect_URI=" + redirectURI : ""}`,
        );
      } else if (code === "invalid_verification_token") {
        setProvisionedVerifyState("invalid_token");
      } else {
        setProvisionedVerifyState("not_found");
      }
    };
    handleProvisionedVerification();
  }, []);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const name = event?.target?.name;
    const key =
      event?.target?.name === "email" ? "emailError" : "passwordError";
    const errorMsg =
      name === "email" ? ERR_MSGS?.Invalid_Email : ERR_MSGS?.Invalid_Password;
    const value = event?.target?.value;
    const regex =
      name === "email" ? regexPatterns?.email : regexPatterns?.password;
    if (!value?.match(regex)) {
      setLoginFormErrors((prev) => ({ ...prev, [key]: errorMsg }));
    }
  };

  const handleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleNavigateToEditProfile = (res: any) => {
    if (res?.roles[0] === USER_ROLES?.CENTER) {
      navigate(
        `/edit-center-profile?${"client_id=" + clientID}${
          "&redirect_uri=" + redirectURI //the redirect_uri is small here
        }${
          cancelURI ? `&${QUERY_PARAMS_KEY.CANCEL_URI}=${cancelURI}` : ""
        }&edit=${true}`,
      );
      return;
    }
    navigate(
      `/edit-user-profile?${"client_id=" + clientID}${
        "&redirect_uri=" + redirectURI //the redirect_uri is small here
      }${
        cancelURI ? `&${QUERY_PARAMS_KEY.CANCEL_URI}=${cancelURI}` : ""
      }&edit=${true}`,
    );
  };

  // Shared "finish login" step: fetches the right profile for the account
  // type (defaulting to the self-signup /users/user lookup, matching every
  // pre-existing password/account-type-picker success path), then redirects
  // with the authCode exactly as a non-MFA login of that type already does.
  // Used both directly on a non-MFA success and after an MFA code is
  // verified, so "lands where a non-MFA login would have" (ticket
  // requirement) holds by construction rather than by keeping two copies
  // of this logic in sync.
  const completeLogin = async (resData: any, accountType?: string) => {
    let profileRes;
    if (accountType === "staff") {
      profileRes = await fetchStaffInfo();
    } else if (accountType === "parent") {
      profileRes = await fetchParentInfo();
    } else {
      profileRes = await fetchAccountInfo();
    }
    if (profileRes?.ok && isEditing) {
      handleNavigateToEditProfile(profileRes?.data);
      clearForm();
      setShowAccountTypeModal(false);
      setMfaChallenge(null);
      return;
    }
    const resolvedAccountType = accountType ?? resData?.account_type;
    window.location.replace(
      `${redirectURI}${"?authCode=" + resData?.authCode}&${
        "remember_me=" + rememberMe
      }${resolvedAccountType ? `&account_type=${resolvedAccountType}` : ""}`,
    );
    clearForm();
    setShowAccountTypeModal(false);
    setMfaChallenge(null);
  };

  // Mirrors onSocialLogin's original (profile-fetch-free) redirect, used
  // both directly and after an MFA code is verified for a social login.
  const completeSocialLogin = (resData: any) => {
    window.location.replace(
      `${redirectURI}?${"authCode=" + resData?.authCode}${
        "&remember_me=" + rememberMe
      }`,
    );
    setMfaChallenge(null);
  };

  // Shared by all three sign-in paths' mfaRequired/mfaEnrollmentRequired
  // branch, mirroring completeLogin/completeSocialLogin's role for the
  // non-MFA success case.
  const openMfaChallenge = (
    resData: any,
    source: MfaSource,
    accountTypeOverride?: string,
  ) => {
    setMfaChallenge({
      challengeId: resData.challengeId,
      accountType: resData.accountType || accountTypeOverride,
      availableChannels: resData.availableChannels || ["email"],
      mode: resData.mfaEnrollmentRequired ? "enroll" : "login",
      source,
    });
  };

  const handleMfaVerified = (resData: any) => {
    if (!mfaChallenge) return;
    if (mfaChallenge.source === "social") {
      completeSocialLogin(resData);
    } else if (mfaChallenge.source === "accountType") {
      completeLogin(resData, mfaChallenge.accountType);
    } else {
      completeLogin(resData);
    }
  };

  const handleSignIn = async () => {
    const res = await signIn(
      redirectURI as string,
      clientID as string,
      loginForm,
      rememberMe,
    );
    if (res?.mfaRequired || res?.mfaEnrollmentRequired) {
      openMfaChallenge(res.data, "password");
    } else if (res?.ok) {
      await completeLogin(res.data);
    } else if (res?.status === 409 && res?.data?.accounts) {
      // Multiple accounts found - show account type selection modal
      setMultipleAccounts(res?.data?.accounts);
      setShowAccountTypeModal(true);
    } else if (res?.data?.error === "user_deactivated") {
      setDeactivationModal("user");
    } else if (res?.data?.error === "center_deactivated") {
      setDeactivationModal("center");
    } else if (res?.data?.error === "parent_deactivated") {
      setDeactivationModal("parent");
    } else if (!res?.isVerified) {
      setShowVerificationModal(true);
    } else setLoginFormErrors((prev) => ({ ...prev, loginError: true }));
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (loginFormErrors?.emailError || loginFormErrors?.passwordError) {
      return;
    } else handleSignIn();
  };

  const handleSelectAccountType = async (accountType: string) => {
    setIsSelectingAccountType(true);
    try {
      const payloadWithAccountType = {
        ...loginForm,
      };
      const res = await signIn(
        redirectURI as string,
        clientID as string,
        payloadWithAccountType,
        rememberMe,
        undefined,
        accountType,
      );
      if (res?.mfaRequired || res?.mfaEnrollmentRequired) {
        openMfaChallenge(res.data, "accountType", accountType);
        setShowAccountTypeModal(false);
      } else if (res?.ok) {
        await completeLogin(res.data, accountType);
      } else if (res?.data?.error === "user_deactivated") {
        setDeactivationModal("user");
        setShowAccountTypeModal(false);
      } else if (res?.data?.error === "center_deactivated") {
        setDeactivationModal("center");
        setShowAccountTypeModal(false);
      } else if (res?.data?.error === "parent_deactivated") {
        setDeactivationModal("parent");
        setShowAccountTypeModal(false);
      } else if (!res?.isVerified) {
        setShowVerificationModal(true);
        setShowAccountTypeModal(false);
      } else {
        setLoginFormErrors((prev) => ({ ...prev, loginError: true }));
        setShowAccountTypeModal(false);
      }
    } catch (error) {
      console.error("Error during account type selection:", error);
      setLoginFormErrors((prev) => ({ ...prev, loginError: true }));
    } finally {
      setIsSelectingAccountType(false);
    }
  };

  const clearForm = () => {
    setLoginForm(initLoginFormCredentials);
    setLoginFormErrors(initLoginFormErrors);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: (credentialResponse) => {
      onSocialLogin("google", credentialResponse?.access_token as string);
      console.log("credentialResponse", credentialResponse);
    },
    onError: () => {
      toast.error("Couldn't Sign In with Google. Try again later");
      console.log("Login Failed");
    },
    flow: "implicit",
  });

  const onSocialLogin = async (provider: string, token: string) => {
    const reqBody = { provider, providerToken: token };
    const res = await signIn(
      redirectURI as string,
      clientID as string,
      reqBody,
      rememberMe,
    );
    if (res?.mfaRequired || res?.mfaEnrollmentRequired) {
      openMfaChallenge(res.data, "social");
    } else if (res?.ok) {
      completeSocialLogin(res.data);
    } else if (res?.data?.error === "user_deactivated") {
      setDeactivationModal("user");
    } else if (res?.data?.error === "center_deactivated") {
      setDeactivationModal("center");
    } else if (res?.data?.error === "parent_deactivated") {
      setDeactivationModal("parent");
    } else if (res?.token) {
      navigate(
        `/complete-profile?${clientID ? "client_id=" + clientID : ""}${
          redirectURI ? "&redirect_URI=" + redirectURI : ""
        }`,
        { state: { socialAccToken: res?.token } },
      );
    } else {
      return;
    }
  };

  const handleNavToSignUp = () => {
    return `/register-option?${clientID ? "client_id=" + clientID : ""}${
      redirectURI ? "&redirect_URI=" + redirectURI : ""
    }`;
  };

  const handleNavToForgotPassword = () => {
    return `/forgot-password?${clientID ? "client_id=" + clientID : ""}${
      redirectURI ? "&redirect_URI=" + redirectURI : ""
    }`;
  };

  if (provisionedVerifyState === "verifying") {
    return (
      <div className="flex justify-center items-center flex-col min-h-screen p-3">
        <img className="mb-9" src="./images/web-logo.png" alt="web-logo" />
        <p className="text-secondary text-lg font-medium">Verifying your account...</p>
      </div>
    );
  }

  if (provisionedVerifyState === "invalid_token") {
    return (
      <div className="flex justify-center items-center flex-col min-h-screen p-3">
        <img className="mb-9" src="./images/web-logo.png" alt="web-logo" />
        <div className="bg-sky border border-sky py-5 md:py-9 px-5 md:px-16 rounded-[20px] max-w-[448px] text-center">
          <h3 className="text-secondary text-2xl font-semibold mb-4">Link expired</h3>
          <p className="text-secondaryVariant text-base">
            This verification link is no longer valid. Please contact your administrator to resend your credentials.
          </p>
        </div>
      </div>
    );
  }

  if (provisionedVerifyState === "not_found") {
    return (
      <div className="flex justify-center items-center flex-col min-h-screen p-3">
        <img className="mb-9" src="./images/web-logo.png" alt="web-logo" />
        <div className="bg-sky border border-sky py-5 md:py-9 px-5 md:px-16 rounded-[20px] max-w-[448px] text-center">
          <h3 className="text-secondary text-2xl font-semibold mb-4">Something went wrong</h3>
          <p className="text-secondaryVariant text-base">
            Unable to verify your account. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-center items-center flex-col min-h-screen overflow-y-auto p-3">
        <div className="">
          <img className="mb-9" src="./images/web-logo.png" alt="web-logo" />

          <div className="bg-sky border border-sky py-5 md:py-9 px-5 md:px-16 rounded-[20px] max-w-[448px]">
            <h3 className="text-secondary text-2xl font-semibold mb-4">
              Welcome back
            </h3>
            <form onSubmit={onSubmit}>
              <div className="mb-6">
                <label className="text-base text-secondaryVariant">
                  Email
                  <RequiredAsterisk />
                </label>
                <input
                  name="email"
                  type="email"
                  placeholder="Enter Your Email"
                  className="px-3 py-3.5 rounded-lg border border-secondaryVariant w-full bg-white mt-2"
                  value={loginForm?.email}
                  onChange={handleInputChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                {loginFormErrors?.emailError && (
                  <p className="text-red-600 pl-5">
                    {loginFormErrors?.emailError}
                  </p>
                )}
              </div>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-base text-secondaryVariant">
                    Password
                    <RequiredAsterisk />
                  </label>
                  <Link
                    to={handleNavToForgotPassword()}
                    className="text-sm text-statusColor"
                  >
                    Forgot your password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    name="password"
                    type={`${showPassword ? "text" : "password"}`}
                    placeholder="Enter Your Password"
                    className="px-3 py-3.5 rounded-lg border border-secondaryVariant w-full bg-white"
                    value={loginForm?.password}
                    onChange={handleInputChange}
                    // onFocus={handleFocus}
                    // onBlur={handleBlur}
                  />
                  <button
                    className="absolute right-2 top-[17px]"
                    type="button"
                    onClick={handleShowPassword}
                  >
                    {showPassword ? (
                      <LuEyeOff className="w-4 h-5 text-secondary" />
                    ) : (
                      <LuEye className="w-4 h-5 text-secondary" />
                    )}
                  </button>
                </div>
                {loginFormErrors?.passwordError && (
                  <p className="text-red-600 pl-5">
                    {loginFormErrors?.passwordError}
                  </p>
                )}
              </div>
              {loginFormErrors?.loginError && (
                <p className="text-red-600 text-center ">
                  {ERR_MSGS.Login_Error}
                </p>
              )}
              <div className="flex items-center gap-1 mb-7">
                <label
                  className="relative flex items-center p-3 rounded-full cursor-pointer"
                  htmlFor="check"
                >
                  <input
                    type="checkbox"
                    className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border border-secondary transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-5 before:w-5 before:-translate-y-2/4 before:-translate-x-2/4 before:opacity-0 before:transition-opacity checked:border-secondary checked:bg-secondary"
                    id="check"
                    onChange={handleRememberMeChange}
                  />
                  <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      stroke="currentColor"
                      stroke-width="1"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                  </span>
                </label>
                <label className="text-base text-secondaryVariant">
                  Save password
                </label>
              </div>
              <button className="btnPrimary" type="submit">
                Login
              </button>
              <p className="flex items-center justify-center gap-3 text-base text-secondaryVariant text-opacity-50 mt-6">
                Don’t have an account?{" "}
                <Link
                  to={handleNavToSignUp()}
                  className=" text-secondary font-medium"
                >
                  Sign up
                </Link>
              </p>
            </form>
          </div>
          <div className="flex items-center px-5 md:px-16 gap-3 flex-col">
            <p className="text-sm text-secondary text-opacity-50 mt-3">OR</p>
            <button
              className="flex items-center gap-3 text-base text-secondary border border-secondaryVariant rounded-lg w-full py-2.5 px-3 hover:shadow-btnShadow"
              type="button"
              onClick={() => googleLogin()}
            >
              <FcGoogle className="w-[26px] h-[26px]" />
              Continue with Google
            </button>
            <FacebookLogin
              appId={fbAppId}
              onSuccess={(response) => {
                onSocialLogin("facebook", response?.accessToken);
              }}
              onFail={(err) => {
                if (err?.status === "loginCancelled") {
                  return;
                }
                toast.error("Couldn't Sign In with Facebook. Try again later");
              }}
              render={(props) => (
                <button
                  className="flex items-center gap-3 text-base text-secondary border border-secondaryVariant rounded-lg w-full py-2.5 px-3 hover:shadow-btnShadow"
                  type="button"
                  onClick={props.onClick}
                >
                  <BiLogoFacebookCircle className="w-[26px] h-[26px] text-[#0866ff]" />
                  Continue with Facebook
                </button>
              )}
            />
            {/* <button className="flex items-center gap-3 text-base text-secondary border border-secondaryVariant rounded-lg w-full py-2.5 px-3 hover:shadow-btnShadow">
              <FaApple className="w-[26px] h-[26px] text-black" />
              Continue with Apple
            </button> */}
            {/* <button onClick={openModal} className="btnPrimary max-w-[200px]">
              Rest Password{" "}
            </button> */}
            <ResetPasswordModal
              isOpen={isModalOpen}
              onClose={closeModal}
              handleNavToForgotPassword={handleNavToForgotPassword}
            />
          </div>
        </div>
        {showVerificationModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 flex justify-center items-center"
            onClick={() => setShowVerificationModal(false)}
          >
            <div
              className=" flex justify-center items-center z-50"
              onClick={(e) => e.stopPropagation()} // Prevent closing on modal click
            >
              <VerifyYourEmailModal
                email={loginForm?.email}
                setShowVerificationModal={setShowVerificationModal}
              />
            </div>
          </div>
        )}
        <DeactivationModal
          isOpen={deactivationModal !== null}
          onClose={() => setDeactivationModal(null)}
          type={deactivationModal}
        />
        <AccountTypeModal
          isOpen={showAccountTypeModal}
          onClose={() => setShowAccountTypeModal(false)}
          accounts={multipleAccounts}
          onSelectAccountType={handleSelectAccountType}
          isLoading={isSelectingAccountType}
        />
        <MfaChallengeModal
          isOpen={mfaChallenge !== null}
          challengeId={mfaChallenge?.challengeId as string}
          availableChannels={mfaChallenge?.availableChannels ?? ["email"]}
          mode={mfaChallenge?.mode ?? "login"}
          rememberMe={rememberMe}
          onClose={() => setMfaChallenge(null)}
          onVerified={handleMfaVerified}
        />
      </div>
    </>
  );
};

export default LoginForm;
